import type { CSSProperties } from "react";
import type { GeneralConfig, LayoutConfig, StyleConfig } from "@/components/landing/registry/types";

// Every value below is a literal, complete Tailwind class string (never
// template-concatenated) so Tailwind's JIT scanner picks it up regardless of
// which enum value is active at runtime.

const BACKGROUND_CLASS: Record<GeneralConfig["background"], string> = {
	auto: "",
	base: "jana-bg-base",
	subtle: "jana-bg-subtle",
	teal: "jana-bg-teal",
	blue: "jana-bg-blue",
	footer: "jana-bg-footer",
};

const DIVIDER_CLASS: Record<GeneralConfig["divider"], string> = {
	none: "",
	top: "jana-divide-top",
	bottom: "jana-divide-bottom",
	both: "jana-divide-top jana-divide-bottom",
};

const PADDING_CLASS: Record<GeneralConfig["paddingY"], string> = {
	sm: "py-10 md:py-14",
	md: "py-16 md:py-24",
	lg: "py-24 md:py-32",
};

const MAX_WIDTH_CLASS: Record<GeneralConfig["maxWidth"], string> = {
	"3xl": "max-w-3xl",
	"4xl": "max-w-4xl",
	"5xl": "max-w-5xl",
	"6xl": "max-w-6xl",
	"7xl": "max-w-7xl",
	full: "max-w-full",
};

const CONTAINER_CLASS: Record<GeneralConfig["containerSize"], string> = {
	narrow: "px-4",
	normal: "px-6",
	wide: "px-8 md:px-12",
};

const SPACING_GAP_CLASS: Record<GeneralConfig["spacing"], string> = {
	tight: "gap-6",
	normal: "gap-10",
	loose: "gap-16",
};

export function generalSectionClassName(general: GeneralConfig): string {
	return [
		BACKGROUND_CLASS[general.background],
		DIVIDER_CLASS[general.divider],
		PADDING_CLASS[general.paddingY],
	]
		.filter(Boolean)
		.join(" ");
}

export function generalContainerClassName(general: GeneralConfig): string {
	return [
		"mx-auto",
		MAX_WIDTH_CLASS[general.maxWidth],
		CONTAINER_CLASS[general.containerSize],
		SPACING_GAP_CLASS[general.spacing],
	].join(" ");
}

const ALIGN_CLASS: Record<LayoutConfig["align"], string> = {
	start: "text-start items-start",
	center: "text-center items-center",
	end: "text-end items-end",
};

const V_ALIGN_CLASS: Record<LayoutConfig["verticalAlign"], string> = {
	start: "items-start",
	center: "items-center",
	end: "items-end",
};

const COLUMNS_CLASS: Record<LayoutConfig["columns"], string> = {
	1: "grid-cols-1",
	2: "grid-cols-1 lg:grid-cols-2",
	3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
};

const GAP_CLASS: Record<LayoutConfig["gap"], string> = {
	sm: "gap-6",
	md: "gap-10",
	lg: "gap-16",
};

const HEIGHT_CLASS: Record<LayoutConfig["height"], string> = {
	auto: "",
	tall: "min-h-[70dvh]",
	screen: "min-h-[100dvh]",
};

const CONTENT_WIDTH_CLASS: Record<LayoutConfig["contentWidth"], string> = {
	narrow: "max-w-2xl",
	normal: "max-w-4xl",
	wide: "max-w-6xl",
};

export function layoutGridClassName(layout: LayoutConfig): string {
	return [
		"grid items-center",
		COLUMNS_CLASS[layout.columns],
		GAP_CLASS[layout.gap],
		V_ALIGN_CLASS[layout.verticalAlign],
		layout.reverse ? "lg:[&>*:first-child]:order-2" : "",
	]
		.filter(Boolean)
		.join(" ");
}

export function layoutWrapClassName(layout: LayoutConfig): string {
	return [
		HEIGHT_CLASS[layout.height],
		ALIGN_CLASS[layout.align],
		CONTENT_WIDTH_CLASS[layout.contentWidth],
	]
		.filter(Boolean)
		.join(" ");
}

const RADIUS_CLASS: Record<StyleConfig["radius"], string> = {
	none: "rounded-none",
	sm: "rounded-lg",
	md: "rounded-2xl",
	lg: "rounded-[1.75rem]",
	"2xl": "rounded-[2.5rem]",
};

const SHADOW_CLASS: Record<StyleConfig["shadow"], string> = {
	none: "",
	sm: "shadow-sm",
	md: "shadow-(--shadow)",
	lg: "shadow-xl",
};

const BORDER_CLASS: Record<StyleConfig["border"], string> = {
	none: "",
	subtle: "border border-border/60",
	solid: "border border-border",
};

const GRADIENT_CLASS: Record<StyleConfig["gradient"], string> = {
	none: "",
	brand: "jana-gradient-text",
	subtle: "",
};

const PATTERN_CLASS: Record<StyleConfig["pattern"], string> = {
	none: "",
	dots: "jana-pattern-dots",
	grid: "jana-pattern-grid",
};

const STYLE_BACKGROUND_CLASS: Record<StyleConfig["background"], string> = {
	none: "",
	gradientWash: "jana-style-gradient-wash",
	patternDots: "jana-pattern-dots",
};

/**
 * Applies to a section's own decorative surface (card/mockup frame), not the
 * whole `<section>` — Radius/Shadow/Glass/Border are meaningless on a bare
 * full-width section, so callers opt a specific inner wrapper into these.
 */
export function surfaceClassName(style: StyleConfig): string {
	return [
		RADIUS_CLASS[style.radius],
		SHADOW_CLASS[style.shadow],
		BORDER_CLASS[style.border],
		style.glass
			? "backdrop-blur-md border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
			: "",
	]
		.filter(Boolean)
		.join(" ");
}

export function decorativeClassName(style: StyleConfig): string {
	return [
		STYLE_BACKGROUND_CLASS[style.background],
		GRADIENT_CLASS[style.gradient],
		PATTERN_CLASS[style.pattern],
	]
		.filter(Boolean)
		.join(" ");
}

export function opacityStyle(style: StyleConfig): CSSProperties {
	return style.opacity < 100 ? { opacity: style.opacity / 100 } : {};
}
